import { useState, useCallback } from 'react';

export interface WizardStepConfig {
  id: string;
  title: string;
  description?: string;
}

interface UseWizardOptions<T extends object> {
  steps: WizardStepConfig[];
  initialData?: Partial<T>;
  onComplete?: (data: T) => Promise<void>;
  onStepChange?: (step: number, data: Partial<T>) => void;
  validateStep?: (step: number, data: Partial<T>) => boolean | string[];
}

interface UseWizardReturn<T extends object> {
  currentStep: number;
  steps: WizardStepConfig[];
  data: Partial<T>;
  errors: Record<string, string>;
  isFirst: boolean;
  isLast: boolean;
  isSubmitting: boolean;
  isComplete: boolean;
  canGoNext: boolean;
  canGoPrevious: boolean;
  goNext: () => Promise<boolean>;
  goPrevious: () => void;
  goToStep: (step: number) => void;
  setStepData: <K extends keyof T>(key: K, value: T[K]) => void;
  setData: (data: Partial<T>) => void;
  setError: (key: string, message: string) => void;
  clearError: (key: string) => void;
  clearAllErrors: () => void;
  reset: () => void;
  complete: () => Promise<void>;
}

export function useWizard<T extends object>(
  options: UseWizardOptions<T>
): UseWizardReturn<T> {
  const { steps, initialData = {}, onComplete, onStepChange, validateStep } = options;

  const [currentStep, setCurrentStep] = useState(0);
  const [data, setDataState] = useState<Partial<T>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  const setStepData = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDataState((prev) => ({ ...prev, [key]: value }));
    // Clear error when user updates the field
    if (errors[key as string]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key as string];
        return newErrors;
      });
    }
  }, [errors]);

  const setData = useCallback((newData: Partial<T>) => {
    setDataState((prev) => ({ ...prev, ...newData }));
  }, []);

  const setError = useCallback((key: string, message: string) => {
    setErrors((prev) => ({ ...prev, [key]: message }));
  }, []);

  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  const goNext = useCallback(async (): Promise<boolean> => {
    // Validate current step if validator provided
    if (validateStep) {
      const result = validateStep(currentStep, data);
      if (result !== true) {
        if (Array.isArray(result)) {
          result.forEach((error) => {
            const [key, message] = error.split(':');
            setError(key, message || 'Invalid value');
          });
        }
        return false;
      }
    }

    if (!isLast) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      onStepChange?.(nextStep, data);
      return true;
    }

    return false;
  }, [currentStep, data, isLast, onStepChange, validateStep, setError]);

  const goPrevious = useCallback(() => {
    if (!isFirst) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      onStepChange?.(prevStep, data);
    }
  }, [currentStep, data, isFirst, onStepChange]);

  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < steps.length && step <= currentStep) {
      setCurrentStep(step);
      onStepChange?.(step, data);
    }
  }, [currentStep, data, steps.length, onStepChange]);

  const reset = useCallback(() => {
    setCurrentStep(0);
    setDataState(initialData);
    setErrors({});
    setIsSubmitting(false);
    setIsComplete(false);
  }, [initialData]);

  const complete = useCallback(async () => {
    if (!onComplete) return;

    setIsSubmitting(true);
    try {
      await onComplete(data as T);
      setIsComplete(true);
    } catch (error) {
      console.error('Wizard completion error:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [data, onComplete]);

  return {
    currentStep,
    steps,
    data,
    errors,
    isFirst,
    isLast,
    isSubmitting,
    isComplete,
    canGoNext: !isLast,
    canGoPrevious: !isFirst,
    goNext,
    goPrevious,
    goToStep,
    setStepData,
    setData,
    setError,
    clearError,
    clearAllErrors,
    reset,
    complete,
  };
}
